// File: src/components/shipping/ShippingMethodsList.js
import React, { useEffect, useMemo, useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import api from "../../api/client";
import logger from "../../utils/logger.js";

export default function ShippingMethodsList({ value, onChange }) {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const labels = useMemo(
    () => ({
      loading: isAr ? "جارِ تحميل طرق الشحن..." : "Loading shipping methods...",
      failed: isAr ? "فشل تحميل طرق الشحن." : "Failed to load shipping methods.",
      none: isAr
        ? "لا توجد طرق شحن متاحة حالياً."
        : "No shipping methods available.",
      free: isAr ? "(مجاني)" : "(Free)",
      etaPrefix: isAr ? "المدة المتوقعة:" : "ETA:",
    }),
    [isAr]
  );

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        if (!mounted) return;
        setLoading(true);
        setError("");

        const data = await api.apiGet("shipping-methods");

        let list = [];

        if (Array.isArray(data?.methods)) {
          list = data.methods;
        } else if (Array.isArray(data?.data)) {
          list = data.data;
        } else if (Array.isArray(data)) {
          list = data;
        } else {
          if (!mounted) return;
          setError(data?.message || labels.failed);
          setMethods([]);
          return;
        }

        list = [...list].sort(
          (a, b) => Number(a.price || 0) - Number(b.price || 0)
        );

        if (!mounted) return;
        setMethods(list);
      } catch (e) {
        logger.error("ShippingMethodsList load error", e);
        if (!mounted) return;
        setError(e?.message || labels.failed);
        setMethods([]);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [labels.failed]);

  const selectedId = value?.id != null ? Number(value.id) : null;

  const handleSelect = (method) => {
    if (!method) return;
    onChange?.(method);
  };

  if (loading) {
    return <div className="form-text">{labels.loading}</div>;
  }

  if (error) {
    return <div className="alert alert-warning py-2 mb-0">{error}</div>;
  }

  if (!methods.length) {
    return <div className="form-text">{labels.none}</div>;
  }

  return (
    <div className={isAr ? "text-end" : "text-start"}>
      {methods.map((m) => {
        const id = Number(m.id);
        const name = isAr ? m.name_ar || m.name_en : m.name_en || m.name_ar;
        const price = Number(m.price || 0);
        const eta = m.estimated_days || "";

        return (
          <div className="form-check mb-2" key={id}>
            <input
              className="form-check-input"
              type="radio"
              name="shipping_method"
              id={`shipping_${id}`}
              checked={selectedId === id}
              onChange={() => handleSelect(m)}
            />
            <label
              className="form-check-label"
              htmlFor={`shipping_${id}`}
              style={{ cursor: "pointer" }}
            >
              <div className="fw-semibold">
                {name}{" "}
                <span className="text-muted">
                  {price > 0 ? `- ${price.toFixed(2)} $` : labels.free}
                </span>
              </div>

              {eta && (
                <div className="text-muted small">
                  {labels.etaPrefix} {eta}
                </div>
              )}
            </label>
          </div>
        );
      })}
    </div>
  );
}